import {Inject, Injectable, InjectionToken} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {EquipmentId, Favourite, FavouriteId} from '@app/graphql-typings/graphql';
import {FavouriteScopeOptions, FavouritesService} from '@app/layout/sitemanager/collectors-favourites/favourites.service';
import {CollectionUtils, MapUtils} from '@shared/util/collections/collection-utils';
import {ObjectUtils} from '@shared/util/general/object-utils';
import {CacheByParentId} from './cache-by-parent-id';

/** Token that determines the fields fetched by the favourites cache fetch. This is the default scope which has no fields added. */
export const FAVOURITES_SCOPE_OPTIONS = new InjectionToken<FavouriteScopeOptions>('FavouritesScopeOptions', {
  providedIn: 'root',
  factory: () => ({})
});

/** Configures whether the favourites cache keeps what it fetches or is just a pass through */
export const FAVOURITES_CACHING_ENABLED = new InjectionToken<boolean>('FavouritesCachingEnabled', {
  providedIn: 'root',
  factory: () => true
});

export abstract class FavouritesCache {
  abstract getFavouritesForEquipment(equipmentId: EquipmentId): Observable<Favourite[]>;
  abstract getFavouritesForEquipmentIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Favourite[]>>;
  abstract getFavouritesByIds(favouriteIds: FavouriteId[], equipmentIds?: EquipmentId[]): Observable<Map<FavouriteId, Favourite>>;
}

/** Caches favourites by equipment id and by favourite id. All methods' returned favourite objects are clones because
 * equipment property is set depending on the equipment that contains them. Cloning prevents mutation of cache object. */
@Injectable()
export class FavouritesCacheImpl implements FavouritesCache {
  private readonly cache: CacheByParentId<EquipmentId, FavouriteId, Favourite>;
  private readonly copy = (favourite: Favourite): Favourite => ObjectUtils.cloneClassInstance(favourite);

  constructor(private readonly favouritesService: FavouritesService,
              @Inject(FAVOURITES_SCOPE_OPTIONS) private readonly scopeOptions: FavouriteScopeOptions,
              @Inject(FAVOURITES_CACHING_ENABLED) cachingEnabled = true) {
    this.cache = new CacheByParentId<EquipmentId, FavouriteId, Favourite>(
      (favouriteIds: FavouriteId[]) => this.fetchByIds(favouriteIds),
      (equipmentIds: EquipmentId[]) => this.fetchByEquipmentIds(equipmentIds),
      cachingEnabled
    );
  }

  getFavouritesForEquipment(equipmentId: EquipmentId): Observable<Favourite[]> {
    return this.getFavouritesForEquipmentIds([equipmentId]).pipe(
      map(favouritesByEquipmentId => favouritesByEquipmentId.get(equipmentId) ?? [])
    );
  }

  getFavouritesForEquipmentIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Favourite[]>> {
    return this.cache.getListByParentIds(equipmentIds).pipe(
      map(favouritesByEquipmentId => MapUtils.mapValues(favouritesByEquipmentId, favourites => favourites.map(this.copy)))
    );
  }

  getFavouritesByIds(favouriteIds: FavouriteId[], equipmentIds?: EquipmentId[]): Observable<Map<FavouriteId, Favourite>> {
    return this.cache.getByIds(favouriteIds, equipmentIds).pipe(map(favouriteById => MapUtils.mapValues(favouriteById, this.copy)));
  }

  private fetchByIds(favouriteIds: FavouriteId[]): Observable<Map<FavouriteId, Favourite>> {
    return this.favouritesService.getFavouriteDetailsByFavIds(favouriteIds, this.scopeOptions).pipe(
      map(favourites => CollectionUtils.indexBy(favourites, 'fav_id')));
  }

  /** Indexes each favourite under only the equipment that was asked for, because a shared favourite also belongs to equipment
   * this call knows nothing about */
  private fetchByEquipmentIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Map<FavouriteId, Favourite>>> {
    return this.favouritesService.getFavouritesForEquipment(equipmentIds, this.scopeOptions).pipe(
      map(favourites => {
        const favouriteByIdByEquipmentId = new Map(equipmentIds.map(equipmentId =>
          <[EquipmentId, Map<FavouriteId, Favourite>]>[equipmentId, new Map<FavouriteId, Favourite>()]));
        favourites.forEach(favourite => favourite.equipment_ids?.forEach(equipmentId =>
          favouriteByIdByEquipmentId.get(equipmentId)?.set(favourite.fav_id, favourite)));
        return favouriteByIdByEquipmentId;
      })
    );
  }
}
